import { createPath } from 'history';
import { parse } from 'qs';
import { ClassAttributes, createElement, FC, Fragment, ReactElement, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router';

import {
  OrganizationDetailFragment,
  ProjectDetailsForShowFragment,
  useAdoptProposalCreationsMutation,
  usePurchaseProposalCreationsMutation,
} from '../../../../generated/graphql';
import { SingleOfferFragment } from '../../../../generated/graphql_offer';
import { getLatestDeliverable } from '../../../../graphql/creation';
import mergeUrlQuery from '../../../../utility/mergeUrlQuery';
import { AdditionalPurchase } from '../../../component/compound/AdditionalPurchase';
import CreationsList, { ProposalItem } from '../../../component/compound/CreationsList';
import { Props as ItemProps } from '../../../component/compound/CreationsList/Item';
import DummyCreationsListItem from '../../../component/compound/DummyCreationsListItem';
import Spinner from '../../../component/compound/Spinner';
import { context as ConfigContext } from '../../../context/config';
import { context as DependencyContext } from '../../../context/dependency';
import { context as historyContext } from '../../../context/history';
import { context as StateContext } from '../../../context/state';

type Props = ClassAttributes<HTMLElement> & {
  project: ProjectDetailsForShowFragment | null;
  offer: SingleOfferFragment | null;
  reload: () => void;
  className?: string;
};

// competitionState: 表示項目の出し分けで使う。通常採用可能/追加購入可能/終了
export type CompetitionState = 'adoptable' | 'purchasable' | 'finalized';

const ProjectProposalList: FC<Props> = ({ project, offer, reload, className }) => {
  const state = useContext(StateContext);
  const dependency = useContext(DependencyContext);
  const profileUrl = useContext(ConfigContext).PROFILE_WEB_URL;
  const history = useContext(historyContext);
  const [updating, setUpdating] = useState(false);
  const [adoptableCount, setAdoptableCount] = useState(0);
  const [competitionState, setCompetitionState] = useState('adoptable' as CompetitionState);

  const query = parse(state.location.search.slice(1));
  const organizationId = query.org;
  let organization: OrganizationDetailFragment;

  const [checkedProposals, setCheckedProposals] = useState([] as string[]);

  if (state.lastAccessedOrganization) {
    organization = state.lastAccessedOrganization;
  } else {
    organization = state.availableOrganizations.items[0];
  }

  // 採用枠の計算とCompetitionStateの判定
  useEffect(() => {
    if (project && project.matchingFormat == 'competition') {
      const creationSet = project.creationSet!;
      if (creationSet.finalizedAt) {
        // finalizedAtが打刻されていればコンペ終了とみなす
        setAdoptableCount(0);
        setCompetitionState('finalized');
        return;
      }
      const adoptedCount = creationSet.creations.filter((c) => c.proposed && c.submittedAt && c.state !== 'canceled')
        .length;
      const additionalAmount = creationSet.additionalAmount ? creationSet.additionalAmount : 0;
      const totalAmount = creationSet.amount + additionalAmount;
      setAdoptableCount(totalAmount - adoptedCount);
      setCompetitionState(totalAmount - adoptedCount > 0 ? 'adoptable' : 'purchasable');
    } else {
      setAdoptableCount(0);
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const [adoptProposalCreations] = useAdoptProposalCreationsMutation();
  const [purchaseProposalCreations] = usePurchaseProposalCreationsMutation();

  // クエパラにorganizationIdが存在しない場合はリダイレクトする
  if (!organizationId && organization) {
    return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
  }

  // 採用処理実行時のspinner
  if (updating) {
    return <Spinner />;
  }

  // リスト取得中のダミーコンテンツ
  if (!offer || !project || !project.creationSet) {
    return (
      <div className={className}>
        <CreationsList proposal={true}>
          {[...new Array(4)].map((_, i) => (<DummyCreationsListItem key={i} />) as ReactElement<ItemProps>)}
        </CreationsList>
      </div>
    );
  }

  const creations = project.creationSet.creations;

  // チェック操作
  const addOrRemoveCheckedProposal = (checked: string) => {
    const newCheckedProposals = checkedProposals.includes(checked)
      ? checkedProposals.filter((cp) => checked !== cp)
      : checkedProposals.concat(checked);
    setCheckedProposals(newCheckedProposals.length >= 1 ? newCheckedProposals : ([] as string[]));
  };

  // 表示するCreation
  const visibleCreations = creations
    .filter((creation) => {
      return creation.proposed && creation.state === 'in_proposal' && getLatestDeliverable(creation);
    })
    .map((c) => {
      return {
        creation: c,
        assignment: offer.assignments.find((a) => a.id == c.assignmentId),
      };
    });

  const productPricing = state.acct.productPricing ? state.acct.productPricing : null;

  return (
    <div className={className}>
      <>
        <AdditionalPurchase
          adoptableCount={adoptableCount}
          competitionState={competitionState}
          organization={organization}
          creationSet={project ? project.creationSet : null}
          email={state.authentication.signedInUser!.email!}
          checkedProposals={checkedProposals}
          onAdoptionClicked={async () => {
            setUpdating(true);
            const result = await adoptProposalCreations({
              variables: {
                projectId: project.id,
                proposalCreationIds: checkedProposals,
              },
            });
            if (result.errors == undefined || result.errors.length == 0) {
              dependency.messageDispatcher.notifyUnimportant('message.adopt_proposal.success');
              reload();
              history.push(`/projects/${project.id}/deliverables`);
            }
            setUpdating(false);
          }}
          onPurchasedWithoutPayment={async () => {
            setUpdating(true);
            const result = await purchaseProposalCreations({
              variables: {
                projectId: project.id,
                proposalCreationIds: checkedProposals,
              },
            });
            if (result.errors == undefined || result.errors.length == 0) {
              dependency.messageDispatcher.notifyUnimportant('message.purchase_proposal_as_addition.success');
              reload();
              history.push(`/projects/${project.id}/deliverables`);
            }
            setUpdating(false);
          }}
          productPricing={productPricing}
          prepareUser={dependency.authenticationUsecase.fetchDirectorUser}
        />

        <CreationsList proposal={true} finalized={!!project.creationSet?.finalizedAt}>
          {visibleCreations.map(({ creation, assignment }) => (
            <ProposalItem
              creation={creation}
              assignment={assignment}
              requestLatestDeliverableCreativeUrl={() => creation.latestDeliverableUrl}
              key={creation.id}
              profileUrl={profileUrl}
              addOrRemoveCheckedProposal={addOrRemoveCheckedProposal}
              checkedList={checkedProposals}
            />
          ))}
        </CreationsList>
      </>
    </div>
  );
};

export default ProjectProposalList;
